import { useCallback, useEffect, useRef, useState } from 'react';
import { useUser } from '@clerk/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { consumerSmsMfaEnrollmentApi, consumerSmsMfaEnrollmentKeys } from '@client/api/waterlily';
import {
  getPhoneVerificationErrorMessage,
  useClerkPhoneVerification,
} from '@client/hooks/useClerkPhoneVerification';

export const CLIENT_SMS_MFA_ENROLLMENT_PATH = '/client/mobile-mfa';

export type ClientSmsMfaStep = 'loading' | 'phone' | 'verify' | 'error';

export function useClientSmsMfaStatus(enabled: boolean) {
  return useQuery({
    queryKey: consumerSmsMfaEnrollmentKeys.status(),
    queryFn: consumerSmsMfaEnrollmentApi.getStatus,
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}

export function useClientSmsMfaEnrollment() {
  const { user, isLoaded } = useUser();
  const queryClient = useQueryClient();
  const status = useClientSmsMfaStatus(isLoaded && !!user);
  const [step, setStep] = useState<ClientSmsMfaStep>('loading');
  const [requestError, setRequestError] = useState<string | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const reservingVerifiedPhoneRef = useRef(false);

  const completeMutation = useMutation({ mutationFn: consumerSmsMfaEnrollmentApi.complete });
  const { mutateAsync: completeEnrollment } = completeMutation;

  const reserveVerifiedPhone = useCallback(async () => {
    setIsCompleting(true);
    setRequestError(null);
    try {
      const result = await completeEnrollment();
      queryClient.setQueryData(consumerSmsMfaEnrollmentKeys.status(), result);
    } catch (error) {
      setRequestError(getPhoneVerificationErrorMessage(error));
      setStep('error');
    } finally {
      setIsCompleting(false);
    }
  }, [completeEnrollment, queryClient]);

  const completeAfterPhoneVerification = useCallback(
    async () => reserveVerifiedPhone(),
    [reserveVerifiedPhone],
  );

  const phoneVerification = useClerkPhoneVerification({
    onVerified: completeAfterPhoneVerification,
    onStepChange: setStep,
  });

  useEffect(() => {
    if (!status.isSuccess || status.data.complete) return;

    if (status.data.verifiedPhoneNumber) {
      if (reservingVerifiedPhoneRef.current) return;
      reservingVerifiedPhoneRef.current = true;
      void reserveVerifiedPhone();
      return;
    }

    setStep('phone');
  }, [reserveVerifiedPhone, status.data, status.isSuccess]);

  useEffect(() => {
    if (!status.isError) return;
    setRequestError(getPhoneVerificationErrorMessage(status.error));
    setStep('error');
  }, [status.error, status.isError]);

  const retry = useCallback(async () => {
    reservingVerifiedPhoneRef.current = false;
    setStep('loading');
    setRequestError(null);
    const refreshed = await status.refetch();
    if (refreshed.data?.verifiedPhoneNumber && !refreshed.data.complete) {
      reservingVerifiedPhoneRef.current = true;
      await reserveVerifiedPhone();
    } else if (refreshed.data && !refreshed.data.complete) {
      setStep('phone');
    }
  }, [reserveVerifiedPhone, status]);

  return {
    ...phoneVerification,
    step,
    status: status.data,
    error: step === 'error' ? requestError : phoneVerification.error,
    isSubmitting: isCompleting || phoneVerification.isSubmitting,
    retry,
  };
}
